// smallCaps.js – Kapitälchen („kleine Grossbuchstaben“) für die Schrift Bandeleart
// Die Schrift hat keine eigenen Kapitälchen. Deshalb werden sie hier selbst gebaut:
// Kleinbuchstaben werden als Grossbuchstaben in kleinerer Schrift (SC_SCALE) geschrieben.
// So sieht es überall gleich aus – in der Vorschau (HTML), im Druckbild (Canvas)
// und auf der Schachtel (SVG). Die Browser würden es sonst unterschiedlich machen.
import React from 'react';

// Grösse der kleinen Grossbuchstaben im Verhältnis zur Schriftgrösse
export const SC_SCALE = 0.72;

// Text in Stücke zerlegen: { t: 'TEXT', small: true/false }
// Beispiel „Anna“ → [{ t: 'A', small: false }, { t: 'NNA', small: true }]
export function scRuns(text) {
  const runs = [];
  for (const ch of String(text ?? '')) {
    const up = ch.toUpperCase();
    const small = up !== ch;            // war ein Kleinbuchstabe (ß wird zu SS)
    const last = runs[runs.length - 1];
    if (last && last.small === small) last.t += up;
    else runs.push({ t: up, small });
  }
  return runs;
}

// ── Canvas ──────────────────────────────────────────────────────────────
// Breite des Textes in Kapitälchen (fs = Schriftgrösse in px, family = Schriftliste)
export function scWidth(ctx, text, fs, family) {
  let w = 0;
  for (const r of scRuns(text)) {
    ctx.font = `${r.small ? fs * SC_SCALE : fs}px ${family}`;
    w += ctx.measureText(r.t).width;
  }
  ctx.font = `${fs}px ${family}`;
  return w;
}

// Text in Kapitälchen zeichnen. y = Grundlinie, align = 'left' | 'center' | 'right'.
// Gibt die Breite zurück.
export function scFill(ctx, text, x, y, fs, family, align = 'left') {
  const w = scWidth(ctx, text, fs, family);
  let cx = align === 'center' ? x - w / 2 : align === 'right' ? x - w : x;
  ctx.save();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  for (const r of scRuns(text)) {
    ctx.font = `${r.small ? fs * SC_SCALE : fs}px ${family}`;
    ctx.fillText(r.t, cx, y);
    cx += ctx.measureText(r.t).width;
  }
  ctx.restore();
  return w;
}

// ── HTML (React) ────────────────────────────────────────────────────────
// <SmallCaps text="Anna" /> → A<span style="font-size:0.72em">NNA</span>
export function SmallCaps({ text }) {
  return scRuns(text).map((r, i) => r.small
    ? React.createElement('span', { key: i, style: { fontSize: `${SC_SCALE}em` } }, r.t)
    : React.createElement(React.Fragment, { key: i }, r.t));
}

// ── SVG ─────────────────────────────────────────────────────────────────
// Inhalt eines <text>-Elements in Kapitälchen (size = Schriftgrösse des Textes).
// esc = Funktion, die Sonderzeichen (&, < …) unschädlich macht.
export function scSvg(text, size, esc) {
  return scRuns(text).map(r => r.small
    ? `<tspan font-size="${+(size * SC_SCALE).toFixed(2)}">${esc(r.t)}</tspan>`
    : `<tspan>${esc(r.t)}</tspan>`).join('');
}
